import * as React from 'react';
import AppTabs from '../components/AppTabs';
import { AppointmentEntity } from '../store/entities/state';
import { useTranslations } from '../store/translations/hooks';
import { TK } from '../store/translations/translationKeys';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import {
    entityEditionCancel,
    loadEntities,
    updateChangingEntity,
    updateEntity,
    openAppointment,
} from '../store/entities/actions';
import { AppointmentInfoContainer, AppointmentInfoLeftContainer, AppointmentPhotoContainer, DateTimeContainer } from './styled';
import Typography from '../components/Typography';
import ButtonsContainer from '../components/ButtonsContainer';
import { getClientAppointmentsAsync, getClientAsync, getClientsAsync, updateAppointmentAsync, updateAppointmentHasHappenedAsync, updateClientInternalNotesAsync, updateClientTagsAsync } from '../ajax/requests';
import { getClient, isSubmitting } from '../store/entities/selectors';
import { AppointmentData, FeatureEnum, PermissionEnum } from '../models';
import { IconButton, Tooltip, Tabs, Tab } from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import WarningIcon from '@material-ui/icons/Warning';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import { getAge } from '../shared/utils';
import { push } from 'connected-react-router';
import { SwitchField, TextInputField } from '../components/Forms/fields';
import { UploadFileModal } from '../components/UploadFileModal';
import { FilesAndResportsList } from '../components/FilesAndReportsList';
import { useGeneralSettings } from '../store/settings/hooks';
import AppointmentsTable from '../components/AppointmentsTable';
import { paths } from '../app/constants';
import { ClientTags } from '../components/Forms/fields/ClientTags';
import { formatPackInfo } from '../components/AppointmentsTable/AppointmentsTable';
import { canEditAppointments, getFeatures, getMyProffesionalData, getPermissions } from '../store/session/selectors';
import { ApplicationState } from '../store';
import { checkProfessionalCanEditAppointment } from '../components/Forms/AppointmentForm/AppointmentForm';
import AppButton from '../components/AppButton';
import AiScriber from '../components/AiScriber';
import { openAiScriber } from '../store/aiScriber/actions';
import { useReportTemplates } from '../hooks/useReportTemplates';

export interface AppointmentProfessionalViewProps {
    data: AppointmentEntity,
}

const AppointmentProfessionalView: React.FC<AppointmentProfessionalViewProps> = ({
    data: appointment,
}) => {
    const t = useTranslations();
    const dispatch = useDispatch();

    const generalSettings = useGeneralSettings()
    const client = useSelector(getClient(appointment.clientId ?? ''))
    const clientSaving = useSelector(isSubmitting('clients'))
    const [showUploadModal, setUploadModal] = useState(false)
    const [internalNotes, setInternalNotes] = useState(client?.internalNotes ?? '')
    const [tab, setTab] = useState<'myNotes'|'appointments'>('myNotes')
    const [allClientAppointments, setAllClientAppointments] = useState<AppointmentData[] | null>(null)
    const [loadingAllClientAppointments, setLoadingAllClientAppointments] = useState<boolean>(false)
    const canEditAllAppointments = useSelector(canEditAppointments)
    const myProfessional = useSelector(getMyProffesionalData)
    const canConfirmAppointments = useSelector((state: ApplicationState) => !getPermissions(state).includes(PermissionEnum.DoNotAllowToConfirmAppointments))
    const canCreateOrEditAppointments = useSelector((state: ApplicationState) => !getPermissions(state).includes(PermissionEnum.DoNotCreateOrEditAppointments))
    const features = useSelector(getFeatures)
    const hasAiScriber = features.includes(FeatureEnum.AiAppointmentsScriber)
    const { templates: reportTemplates } = useReportTemplates()

    const canEditAppointment = (canEditAllAppointments || (myProfessional && checkProfessionalCanEditAppointment(appointment, myProfessional.id, generalSettings))) && canCreateOrEditAppointments

    useEffect(() => {
        const clientId = appointment.clientId
        if (!clientId) return
        dispatch(loadEntities('clients', () => getClientAsync(clientId).then(c => c ? [c] : []), 'AppendResult'))
    }, [dispatch, appointment.clientId])

    useEffect(() => {
        setInternalNotes(client?.internalNotes ?? '')
    }, [client?.id, client?.internalNotes])

    useEffect(() => {
        if (client && !allClientAppointments && !loadingAllClientAppointments) {
            const from = new Date()
            from.setDate(from.getDate() - 365)
            const to = new Date()
            to.setDate(from.getDate() + 365)

            setLoadingAllClientAppointments(true)
            getClientAppointmentsAsync(client.id, from, to)
                .then(setAllClientAppointments)
                .finally(() => setLoadingAllClientAppointments(false))
        }
    }, [client])

    const handleClose = useCallback(() => {
        dispatch(entityEditionCancel('appointments'));
    }, [dispatch, appointment.id]);

    const handleEditClick = useCallback(() => {
        dispatch(openAppointment(appointment.id))
        dispatch(updateChangingEntity('appointments', appointment)) // Force to exit professional view
    }, [dispatch, appointment])

    const handleOpenClient = useCallback(() => {
        dispatch(entityEditionCancel('appointments'));

        const clientId = appointment.clientId
        if (!clientId) return

        dispatch(loadEntities('clients', (pageSize) => getClientsAsync(`"${appointment.clientName}"`, pageSize), 'NewResult'));
        dispatch(push({pathname: paths.clients(), search: `?id=${clientId}`}));
    }, [dispatch, appointment.clientId, appointment.clientName])

    const handleHasHappenedChange = useCallback((hasHappened: boolean) => {
        dispatch(updateEntity('appointments', {...appointment, hasHappened}, () => updateAppointmentHasHappenedAsync(appointment.id, hasHappened)))
    }, [dispatch, appointment])

    const handleGroupClientAttendedChange = useCallback((index: number) => {
        const updated = (appointment.groupClients ?? []).map((gc, i) => i !== index ? gc : {
            ...gc,
            attended: gc.attended === undefined ? true : gc.attended === true ? false : undefined,
        });
        const next = {...appointment, groupClients: updated};
        dispatch(updateEntity('appointments', next, () => updateAppointmentAsync(appointment.id, {data: next}), true));
    }, [dispatch, appointment])

    const handleConfirmAllParticipants = useCallback(() => {
        const updated = (appointment.groupClients ?? []).map(gc => ({...gc, attended: true as const}));
        const next = {...appointment, groupClients: updated};
        dispatch(updateEntity('appointments', next, () => updateAppointmentAsync(appointment.id, {data: next}), true));
    }, [dispatch, appointment])

    const handleOpenAiScriber = useCallback(() => {
        dispatch(openAiScriber(appointment.id));
    }, [dispatch, appointment.id]);

    const handleSaveInternalNotes = useCallback(() => {
        if (!client) return
        dispatch(updateEntity('clients', {...client, internalNotes}, () => updateClientInternalNotesAsync(client.id, internalNotes)))
    }, [dispatch, client, internalNotes])

    const handleSaveTags = useCallback((tags: string[]) => {
        if (!client) return
        dispatch(updateEntity('clients', {...client, tags}, () => updateClientTagsAsync(client.id, tags)))
    }, [dispatch, client])

    const appointmentsAfterThis = allClientAppointments?.filter(a => a.date > appointment.date).length ?? Number.NaN

    return (
        <div>
            { appointmentsAfterThis === 0 && 
                <Typography block size='medium' style={{padding: 'var(--spacer-S) 0', marginBottom: 'var(--spacer-S)', borderRadius: '3px', backgroundColor: 'var(--danger)', color: 'white', textAlign: 'center'}}><b>{t(TK.lastClientAppointment)}!</b></Typography>
            }
            { appointmentsAfterThis === 1 && 
                <Typography block size='medium' style={{padding: 'var(--spacer-S) 0', marginBottom: 'var(--spacer-S)', borderRadius: '3px', backgroundColor: 'var(--orange)', color: 'white', textAlign: 'center'}}><b>{t(TK.penultimateClientAppointment)}</b></Typography>
            }
            <AppointmentInfoContainer>
                <AppointmentPhotoContainer>
                    {client?.photo && <img className="photo" alt="" src={`data:image/png;base64,${client.photo}`}/>}
                </AppointmentPhotoContainer>
                {!!client && <>
                    <AppointmentInfoLeftContainer>
                        <Typography id="client-name">
                            <AppButton id="open-client-button" style={{padding: 0, verticalAlign: 'top'}} color="link" onClick={handleOpenClient}>
                                {<b>{appointment.clientName}</b>}
                            </AppButton>
                        </Typography>
                        {client?.birthday && <Typography id="client-age" size="small">{getAge(client.birthday)} {t(TK.yearsOld)}</Typography>}
                        {client?.job && <Typography id="client-job" size="small">{client.job}</Typography>}
                        {client?.subsystemName && <Typography id="client-subsystem-name" size="small">{client.subsystemName}</Typography>}
                        {appointment.packAppointmentPosition && <Typography id="appointment-pack" size="small">{formatPackInfo(appointment)}</Typography>}
                        <ClientTags values={client?.tags ?? []} onChange={handleSaveTags} disabled={clientSaving}/>
                        {client?.warningNote && <span style={{ color: 'var(--danger-color)', fontSize: '0.8em', display: 'flex', alignItems: 'center', gap: '2px' }}><WarningIcon style={{ fontSize: '0.9em' }} />{client.warningNote}</span>}
                        {appointment?.notes && <><br/><Typography id="appointment-notes"><b>{t(TK.notes)}:</b> {appointment.notes}</Typography></>}
                    </AppointmentInfoLeftContainer>
                </>}
                <DateTimeContainer>
                    <Typography id="appointment-date"><b>{appointment.date}</b></Typography>
                    <Typography id="appointment-start" size="small">{appointment.start} - {appointment.end}</Typography>
                    <Typography id="appointment-room" size="small">{appointment.room}</Typography>
                </DateTimeContainer>
            </AppointmentInfoContainer>
            
            {(appointment.groupClients?.length ?? 0) > 0 && <>
                <hr/>
                <div style={{display: 'flex', alignItems: 'center', gap: 'var(--spacer-XXS)', marginBottom: 'var(--spacer-XS)'}}>
                    <Typography style={{fontWeight: 500}}>{t(TK.participants)}</Typography>
                    <Tooltip title={t(TK.confirmAllParticipants)}>
                        <IconButton size="small" style={{color: 'var(--success-color)'}} onClick={handleConfirmAllParticipants}>
                            <CheckCircleOutlineIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                </div>
                {appointment.groupClients!.map((gc, index) => (
                    <div key={index} style={{display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)', padding: 'var(--spacer-XXS) var(--spacer-XS)', backgroundColor: 'var(--bg-light)', borderRadius: '4px', marginBottom: 'var(--spacer-XXS)'}}>
                        <Typography size="small" style={{minWidth: '1.2rem', color: 'var(--text-muted)', flexShrink: 0}}>{index + 1}.</Typography>
                        <div style={{display: 'flex', alignItems: 'center', gap: 'var(--spacer-XXS)', flexGrow: 1}}>
                            <IconButton
                                size="small"
                                style={{
                                    padding: 2,
                                    color: gc.attended === true
                                        ? 'var(--success-color)'
                                        : gc.attended === false
                                            ? 'var(--danger-color)'
                                            : 'var(--text-muted)',
                                }}
                                onClick={() => handleGroupClientAttendedChange(index)}
                            >
                                {gc.attended === false ? <CancelOutlinedIcon fontSize="small"/> : <CheckCircleOutlineIcon fontSize="small"/>}
                            </IconButton>
                            <Typography>{gc.clientName ?? '—'}</Typography>
                        </div>
                    </div>
                ))}
            </>}

            {appointment.clientId && <>
                <hr/>

                <AppTabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary" textColor="primary" style={{ borderBottom: 'var(--border-thin) solid var(--appLigthGrey)' }}>
                    <Tab label={t(TK.internalNotes)} value="myNotes" />
                    <Tab label={t(TK.appointments)} value="appointments" />
                </AppTabs>

                {tab === 'myNotes' && <TextInputField type='textarea' value={internalNotes} onChange={setInternalNotes}/>}
                {tab === 'appointments' && <AppointmentsTable
                    appointments={allClientAppointments ?? []}
                    isLoading={loadingAllClientAppointments}
                    settings={{hideColumns: ['price', 'effectivePrice', 'client', 'actionButtons', 'extras', 'pack', 'payment']}}
                    // onEditAppointment={a => {
                    //     dispatch(entityEditionCancel('appointments'))
                    //     dispatch(entityEditing('appointments', a.id))
                    // }}
                />}

                {internalNotes !== (client?.internalNotes ?? '') && 
                    <div style={{marginTop: 'var(--spacer-XS)', textAlign: 'end'}}>
                        <AppButton color='success' onClick={handleSaveInternalNotes}>{t(TK.save)}</AppButton>
                    </div>
                }

                <hr/>

                <div>
                    {!!appointment.clientId && (
                        <FilesAndResportsList
                            clientId={appointment.clientId}
                            showActiveTasksSection
                            showHistoryTitle
                            appointment={appointment}
                            onOpenAiScriber={hasAiScriber ? handleOpenAiScriber : undefined}
                        />
                    )}
                    {hasAiScriber && !!appointment.clientId && (
                        <AiScriber
                            appointmentId={appointment.id}
                            templates={reportTemplates}
                        />
                    )}
                </div>
            </>}

            <hr/>

            <ButtonsContainer>
                {!generalSettings.doNotUseAppointmentConfirmation && canConfirmAppointments && <>
                    <Typography>{t(TK.happened)+'?'}</Typography>
                    <SwitchField
                        value={appointment.hasHappened}
                        trueText={t(TK.yes)}
                        falseText={t(TK.no)}
                        onChange={handleHasHappenedChange}
                    />
                </>}
                {canEditAppointment && <AppButton color="success" onClick={handleEditClick}>{t(TK.edit)}</AppButton>}
                <AppButton onClick={handleClose}>{t(TK.close)}</AppButton>
            </ButtonsContainer>

            {!!appointment.clientId && <UploadFileModal clientId={appointment.clientId} show={showUploadModal} close={() => setUploadModal(false)}/>}
        </div>
    );
}

export default AppointmentProfessionalView;
